// ETP Campaign Quiz — sequential 3-step decision tree.
// Key order in the recommendation lookup: stage,party,path.

const NO_MATCH = 'No matching recommendation found.';
const LABEL_CAMPAIGN = 'Campaign';
const LABEL_POST = 'Post-campaign';
const REASONING_PREFIX = 'Reasoning: ';

// Recommendation strings stored as one line, e.g.:
//   "Campaign: X. Post-campaign: Y.<br><br><b>Campaign reasoning:</b> A.
//    <br><br><b>Post-campaign reasoning:</b> B."
// We split into two structured blocks (Campaign / Post-campaign) so the result
// can render as a clean bullet list with bold labels and inline reasoning.
function parseRecommendation(raw) {
  if (!raw) return [];

  const parts = raw.split(/<br>\s*<br>/i);
  const main = (parts[0] || '').trim();

  const postSplit = main.split(/\s*Post-campaign:\s*/i);
  const campaignText = (postSplit[0] || '').replace(/^\s*Campaign:\s*/i, '').trim();
  const postText = (postSplit[1] || '').trim();

  let campaignReasoning = '';
  let postReasoning = '';
  for (let i = 1; i < parts.length; i++) {
    const chunk = parts[i].trim();
    if (/<b>\s*Campaign reasoning/i.test(chunk)) {
      campaignReasoning = chunk.replace(/<b>.*?<\/b>\s*/i, '').trim();
    } else if (/<b>\s*Post-campaign reasoning/i.test(chunk)) {
      postReasoning = chunk.replace(/<b>.*?<\/b>\s*/i, '').trim();
    }
  }

  return [
    { label: LABEL_CAMPAIGN, body: campaignText, reasoning: campaignReasoning },
    { label: LABEL_POST,     body: postText,     reasoning: postReasoning },
  ];
}

function renderResult(listEl, raw) {
  const items = parseRecommendation(raw);
  if (!items.length) {
    listEl.innerHTML = `<li class="etp-campaign-quiz__result-item">${NO_MATCH}</li>`;
    return;
  }

  listEl.innerHTML = items.map((item) => {
    const reasoning = item.reasoning ? ` ${REASONING_PREFIX}${item.reasoning}` : '';
    // body may already end with a period; trim trailing dots for a clean join.
    const body = (item.body || '').replace(/\.\s*$/, '. ');
    return `
      <li class="etp-campaign-quiz__result-item">
        <strong class="etp-campaign-quiz__result-label">${item.label}</strong>
        <p class="etp-campaign-quiz__result-body">${body}${reasoning}</p>
      </li>
    `;
  }).join('');
}

function setEnabled(select, enabled) {
  if (!select) return;
  if (enabled) select.removeAttribute('disabled');
  else select.setAttribute('disabled', '');
}

export function initCampaignQuiz(scope) {
  const root = scope || document;
  const quizzes = new Set();
  if (root.matches && root.matches('.etp-campaign-quiz')) quizzes.add(root);
  root.querySelectorAll('.etp-campaign-quiz').forEach((el) => quizzes.add(el));

  quizzes.forEach((quiz) => {
    if (quiz.dataset.etpQuizReady) return;
    quiz.dataset.etpQuizReady = 'true';

    let table = {};
    try {
      table = JSON.parse(quiz.dataset.recommendations || '{}');
    } catch (e) {
      console.warn('[ETP campaign-quiz] could not parse recommendations.', e);
      return;
    }

    const stageSelect = quiz.querySelector('[data-field="stage"]');
    const pathSelect  = quiz.querySelector('[data-field="path"]');
    const partySelect = quiz.querySelector('[data-field="party"]');

    const resultBlock = quiz.querySelector('[data-result]');
    const resultList  = resultBlock ? resultBlock.querySelector('.etp-campaign-quiz__result-list') : null;

    if (!stageSelect || !pathSelect || !partySelect || !resultBlock || !resultList) return;

    const update = () => {
      const stage = stageSelect.value;
      const path  = pathSelect.value;
      const party = partySelect.value;

      // Sequential gating: later selects are usable only once the earlier
      // one has an answer. Avoids stale keys when users skip ahead.
      setEnabled(pathSelect, !!stage);
      setEnabled(partySelect, !!stage && !!path);

      if (stage && path && party) {
        const key = `${stage},${party},${path}`;
        renderResult(resultList, table[key] || '');
        resultBlock.removeAttribute('hidden');
      } else {
        resultList.innerHTML = '';
        resultBlock.setAttribute('hidden', '');
      }
    };

    // Reset later answers whenever an earlier one changes so the result
    // never reflects a partially-stale combination.
    stageSelect.addEventListener('change', () => {
      pathSelect.value = '';
      partySelect.value = '';
      update();
    });
    pathSelect.addEventListener('change', () => {
      partySelect.value = '';
      update();
    });
    partySelect.addEventListener('change', update);
  });
}
